class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (evt) => evt.target?.id === 'CartDrawer-Overlay' && this.close());
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.scheduleFocusTrap();

    document.body.classList.add('overflow-hidden');

    // `view-event-trigger="manual"` skips the cart:view event on connect; fire
    // it when the drawer actually opens. The event helper retries independently
    // so analytics module timing can never interrupt the cart controls.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.cancelFocusTrap();
    const activeElement = this.activeElement;
    this.classList.remove('active');
    queueMicrotask(() => removeTrapFocus(activeElement));
    document.body.classList.remove('overflow-hidden');
  }

  scheduleFocusTrap() {
    this.cancelFocusTrap();
    const drawerInner = this.querySelector('.drawer__inner');
    this.focusTrapHandler = (event) => {
      if (event.target === drawerInner && event.propertyName === 'transform') this.activateFocusTrap();
    };
    this.addEventListener('transitionend', this.focusTrapHandler);
    const delay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 380;
    this.focusTrapTimer = window.setTimeout(() => this.activateFocusTrap(), delay);
  }

  activateFocusTrap() {
    this.cancelFocusTrap();
    if (!this.classList.contains('active')) return;
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = containerToTrapFocusOn?.querySelector('.drawer__close') || containerToTrapFocusOn;
    if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
  }

  cancelFocusTrap() {
    if (this.focusTrapTimer) window.clearTimeout(this.focusTrapTimer);
    if (this.focusTrapHandler) this.removeEventListener('transitionend', this.focusTrapHandler);
    this.focusTrapTimer = undefined;
    this.focusTrapHandler = undefined;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];

    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    const cartLiveRegion = document.getElementById('cart-live-region-text');

    if (mainCartItems) {
      sections.push({ id: 'main-cart-items', section: mainCartItems.dataset.id, selector: '.js-contents' });
    }
    if (mainCartFooter) {
      sections.push({ id: 'main-cart-footer', section: mainCartFooter.dataset.id, selector: '.js-contents' });
    }
    if (cartLiveRegion) {
      sections.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
    }

    return sections;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
